import { movedChipLabel, moveTargetLabel, skippedChipLabel } from "../lib/calendarCopy";
import type { ScheduleOccurrence } from "../types";

/** A stable, unique React/Preact `key` for an occurrence — cadence-kind
 * occurrences carry NO synthetic id (ADR-002 A5 decision 1.3, addressed by
 * the `(cadence_id, date)` tuple instead), so the key must be built from
 * whatever identity each kind DOES carry. */
export function occurrenceKey(occurrence: ScheduleOccurrence): string {
  const identity = occurrence.cadence_id ?? occurrence.id ?? occurrence.title;
  return `${occurrence.kind}:${identity}:${occurrence.date}`;
}

export interface ChipMeta {
  moved: boolean;
  movedTargetLabel?: string;
}

/** Resolves the two cross-occurrence lookups §2.5's chip rendering needs
 * (see `hooks/useSchedule.ts`'s own docstring on why these are reverse
 * indexes rather than fields on the occurrence itself). */
export function resolveChipMeta(
  occurrence: ScheduleOccurrence,
  assignmentDateById: Map<string, string>,
  movedFromByAssignmentId: Map<string, boolean>,
  thisWeekDays: Date[],
): ChipMeta {
  const moved =
    occurrence.kind === "assignment" && occurrence.id
      ? Boolean(movedFromByAssignmentId.get(occurrence.id))
      : false;
  let movedTargetLabel: string | undefined;
  if (occurrence.exception?.action === "moved" && occurrence.exception.linked_assignment_id) {
    const targetDate = assignmentDateById.get(occurrence.exception.linked_assignment_id);
    movedTargetLabel = targetDate ? moveTargetLabel(targetDate, thisWeekDays) : "another day";
  }
  return { moved, movedTargetLabel };
}

export interface CalendarChipButtonProps {
  occurrence: ScheduleOccurrence;
  /** True when this occurrence IS the linked one-off of a `moved`
   * exception elsewhere on the grid — §2.5's small trailing "moved" tag on
   * the occurrence's NEW date. */
  moved: boolean;
  /** Only meaningful when `occurrence.exception?.action === "moved"` — the
   * §2.8 `moveTargetLabel` for the chip's own ghost label on its nominal
   * date ("<title> — moved to <label>"). */
  movedTargetLabel?: string;
  onTap: (occurrence: ScheduleOccurrence) => void;
}

/** §2.5's chip anatomy/status table, one real `<button>` per occurrence —
 * shared by the month table, week columns, and phone agenda (never a
 * JS-only clickable `<div>`, matching `DataTable`'s own primary-cell
 * rule). Every modifier class here composes with the base `.calendar-chip`
 * (§7's own token/class summary lists them as `.calendar-chip` "+"
 * modifiers, not standalone replacements). */
function chipClass(occurrence: ScheduleOccurrence): string {
  if (occurrence.kind === "health_event") return "calendar-chip calendar-chip-health";
  if (occurrence.exception?.action === "suppressed") return "calendar-chip is-ghost is-skipped";
  if (occurrence.exception?.action === "moved") return "calendar-chip is-ghost";
  if (occurrence.status === "done") return "calendar-chip is-done";
  if (occurrence.status === "overdue") return "calendar-chip is-overdue";
  // §2.5's `.is-scheduled` row is explicitly scoped to "scheduled
  // (fixed-recurrence, future date)" — cadence-kind only. A dated
  // assignment (including the linked one-off on its new date, §2.5's own
  // "plain .calendar-chip, same rendering as any dated assignment" line)
  // never gets the dimmed treatment, even when its own status happens to
  // be "scheduled" too.
  if (occurrence.status === "scheduled" && occurrence.kind === "cadence")
    return "calendar-chip is-scheduled";
  return "calendar-chip";
}

function chipLabel(occurrence: ScheduleOccurrence, movedTargetLabel?: string): string {
  if (occurrence.exception?.action === "suppressed") return skippedChipLabel(occurrence.title);
  if (occurrence.exception?.action === "moved") {
    return movedChipLabel(occurrence.title, movedTargetLabel ?? "another day");
  }
  return occurrence.title;
}

export function CalendarChipButton({
  occurrence,
  moved,
  movedTargetLabel,
  onTap,
}: CalendarChipButtonProps) {
  return (
    <button
      type="button"
      class={chipClass(occurrence)}
      data-occurrence-kind={occurrence.kind}
      data-occurrence-status={occurrence.status}
      onClick={() => onTap(occurrence)}
    >
      {chipLabel(occurrence, movedTargetLabel)}
      {moved ? <span class="calendar-chip-moved-tag"> moved</span> : null}
    </button>
  );
}
